import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { RefreshCw, Rocket } from "lucide-react";

import { GlobalAppMenu } from "@/components/nav/GlobalAppMenu";
import { apiClient } from "@/services/api/client";
import { useWatchlistsQuery } from "@/features/watchlist/queries";
import { useBootstrapQuery } from "@/features/market-data/queries";
import {
  useQuantPanelBootstrapQuery,
  useQuantPanelRunLogsQuery,
  useQuantPanelRunQuery,
  useQuantPanelRunsQuery,
  useQuantPanelRunSelectsQuery,
  useQuantSelectsQuery,
} from "@/features/workbench/queries";
import type { QuantPanelRun } from "@/services/api/types";

interface QuantFormState {
  presetKey: string;
  pickDate: string;
  limit: number | null;
  minScore: number | null;
  maxStocks: number | null;
}

type QuantSortKey = "score_desc" | "score_asc" | "code_asc" | "code_desc";

function toRunId(raw: string | number) {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatTimestamp(raw: string | null | undefined) {
  const value = String(raw || "").trim();
  if (!value) {
    return "--";
  }
  return value;
}

function formatTradeDate(raw: string | null | undefined) {
  const value = String(raw || "").trim();
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  return value || "--";
}

function inputDateToYmd(raw: string) {
  const value = String(raw || "").trim();
  if (!value) {
    return "";
  }
  const digits = value.replaceAll("-", "");
  return /^\d{8}$/.test(digits) ? digits : "";
}

function ymdToInput(raw: string | null | undefined) {
  const value = String(raw || "").trim();
  if (!/^\d{8}$/.test(value)) {
    return "";
  }
  return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
}

function statusTone(status: string | null | undefined) {
  const value = String(status || "").toUpperCase();
  if (["DONE", "SUCCESS", "COMPLETED"].includes(value)) {
    return "text-emerald-300";
  }
  if (["FAILED", "ERROR", "CANCELLED"].includes(value)) {
    return "text-rose-300";
  }
  if (["RUNNING", "QUEUED"].includes(value)) {
    return "text-amber-300";
  }
  return "text-slate-300";
}

function toNumber(value: unknown, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toNumberOrNull(value: string, fallback: number) {
  const raw = String(value || "").trim();
  if (!raw) {
    return null;
  }
  return toNumber(raw, fallback);
}

export function QuantWorkbenchPage() {
  const queryClient = useQueryClient();
  const bootstrapQuery = useQuantPanelBootstrapQuery();
  const terminalBootstrapQuery = useBootstrapQuery();
  const runsQuery = useQuantPanelRunsQuery(40);
  const watchlistsQuery = useWatchlistsQuery();
  const currentUser = terminalBootstrapQuery.data?.current_user;
  const displayUserName = currentUser?.display_name || currentUser?.email || "Guest";

  const [form, setForm] = useState<QuantFormState>({
    presetKey: "balanced",
    pickDate: "",
    limit: 500,
    minScore: 50,
    maxStocks: 6000,
  });
  const [formInitialized, setFormInitialized] = useState(false);

  useEffect(() => {
    if (formInitialized || !bootstrapQuery.data) {
      return;
    }
    const defaults = bootstrapQuery.data.defaults || {};
    const presetKey = String(defaults.preset_key || bootstrapQuery.data.presets[0]?.key || "balanced");
    setForm({
      presetKey,
      pickDate: ymdToInput(String(defaults.pick_date || "")),
      limit: toNumber(defaults.limit, 500),
      minScore: toNumber(defaults.min_score, 50),
      maxStocks: toNumber(defaults.max_stocks, 6000),
    });
    setFormInitialized(true);
  }, [bootstrapQuery.data, formInitialized]);

  const runs = useMemo(() => {
    if (runsQuery.data && runsQuery.data.length) {
      return runsQuery.data;
    }
    return bootstrapQuery.data?.runs || [];
  }, [bootstrapQuery.data?.runs, runsQuery.data]);

  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  useEffect(() => {
    if (!runs.length) {
      setActiveRunId(null);
      return;
    }
    const valid = activeRunId != null && runs.some((item) => toRunId(item.id) === activeRunId);
    if (!valid) {
      setActiveRunId(toRunId(runs[0].id));
    }
  }, [activeRunId, runs]);

  const activeRun = useMemo<QuantPanelRun | null>(
    () => runs.find((item) => toRunId(item.id) === activeRunId) || null,
    [activeRunId, runs],
  );
  const runDetailQuery = useQuantPanelRunQuery(activeRunId);
  const runLogsQuery = useQuantPanelRunLogsQuery(activeRunId, { cursor: 0, limit: 200 });
  const runSelectsQuery = useQuantPanelRunSelectsQuery(activeRunId, 300);
  const baselineSelectsQuery = useQuantSelectsQuery(300, "", activeRunId == null);
  const shownSelects = activeRunId != null ? runSelectsQuery.data : baselineSelectsQuery.data;
  const shownTradeDate = activeRunId != null ? runDetailQuery.data?.trade_date : baselineSelectsQuery.data?.trade_date;
  const watchlists = watchlistsQuery.data?.watchlists || [];
  const defaultWatchlistId = watchlistsQuery.data?.default_watchlist_id ?? (watchlists[0]?.id || null);

  const [stageFilter, setStageFilter] = useState<"all" | "accumulation_end" | "markup_early">("all");
  const [keywordFilter, setKeywordFilter] = useState("");
  const [scoreMinFilterInput, setScoreMinFilterInput] = useState("0");
  const [sortKey, setSortKey] = useState<QuantSortKey>("score_desc");
  const [rowLimit, setRowLimit] = useState(100);
  const [targetWatchlistId, setTargetWatchlistId] = useState<number | null>(null);
  const [selectedSymbols, setSelectedSymbols] = useState<Record<string, boolean>>({});
  const scoreMinFilter = useMemo(() => {
    const raw = scoreMinFilterInput.trim();
    if (!raw) {
      return 0;
    }
    return toNumber(raw, 0);
  }, [scoreMinFilterInput]);

  useEffect(() => {
    if (targetWatchlistId != null) {
      return;
    }
    if (defaultWatchlistId != null) {
      setTargetWatchlistId(Number(defaultWatchlistId));
    }
  }, [defaultWatchlistId, targetWatchlistId]);

  const filteredRows = useMemo(() => {
    const rows = (shownSelects?.items || []).filter((item) => {
      const stageOk = stageFilter === "all" || String(item.stage_tag || "") === stageFilter;
      if (!stageOk) {
        return false;
      }
      const score = Number(item.score || 0);
      if (score < scoreMinFilter) {
        return false;
      }
      const keyword = keywordFilter.trim().toUpperCase();
      if (!keyword) {
        return true;
      }
      const code = String(item.ts_code || "").toUpperCase();
      const name = String(item.stock_name || "").toUpperCase();
      return code.includes(keyword) || name.includes(keyword);
    });
    rows.sort((a, b) => {
      const scoreA = Number(a.score || 0);
      const scoreB = Number(b.score || 0);
      if (sortKey === "score_asc") {
        return scoreA - scoreB;
      }
      if (sortKey === "score_desc") {
        return scoreB - scoreA;
      }
      const codeA = String(a.ts_code || "");
      const codeB = String(b.ts_code || "");
      if (sortKey === "code_asc") {
        return codeA.localeCompare(codeB);
      }
      return codeB.localeCompare(codeA);
    });
    return rows.slice(0, Math.max(20, Math.min(500, rowLimit)));
  }, [keywordFilter, rowLimit, scoreMinFilter, shownSelects?.items, sortKey, stageFilter]);

  const selectedCount = useMemo(
    () => filteredRows.filter((item) => selectedSymbols[String(item.ts_code || "").toUpperCase()]).length,
    [filteredRows, selectedSymbols],
  );
  const selectedSymbolsList = useMemo(
    () =>
      filteredRows
        .map((item) => String(item.ts_code || "").toUpperCase())
        .filter((symbol) => selectedSymbols[symbol]),
    [filteredRows, selectedSymbols],
  );

  useEffect(() => {
    const visibleCodes = new Set(filteredRows.map((item) => String(item.ts_code || "").toUpperCase()));
    setSelectedSymbols((prev) => {
      const next: Record<string, boolean> = {};
      for (const [code, checked] of Object.entries(prev)) {
        if (checked && visibleCodes.has(code)) {
          next[code] = true;
        }
      }
      return next;
    });
  }, [filteredRows]);

  const runMutation = useMutation({
    mutationFn: () =>
      apiClient.createQuantPanelRun({
        preset_key: form.presetKey || "balanced",
        pick_date: inputDateToYmd(form.pickDate),
        limit: Math.max(20, Math.min(2000, Math.round(form.limit ?? 500))),
        min_score: Math.max(0, Math.min(100, Number(form.minScore ?? 50))),
        max_stocks: Math.max(200, Math.min(8000, Math.round(form.maxStocks ?? 6000))),
      }),
    onSuccess: async (payload) => {
      const runId = Number(payload.run_id || 0);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["quant", "panel", "bootstrap"] }),
        queryClient.invalidateQueries({ queryKey: ["quant", "panel", "runs"] }),
      ]);
      if (runId > 0) {
        setActiveRunId(runId);
      }
    },
  });

  const addSelectedToWatchlistMutation = useMutation({
    mutationFn: async (watchlistId: number) => {
      if (!selectedSymbolsList.length) {
        throw new Error("no_symbols_selected");
      }
      return apiClient.bulkAddWatchlistItems(
        watchlistId,
        selectedSymbolsList.map((symbol) => ({ symbol })),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      setSelectedSymbols({});
    },
  });
  const createWatchlistAndAddMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!selectedSymbolsList.length) {
        throw new Error("no_symbols_selected");
      }
      const watchlist = await apiClient.createWatchlist({ name });
      await apiClient.bulkAddWatchlistItems(
        Number(watchlist.id),
        selectedSymbolsList.map((symbol) => ({ symbol })),
      );
      return watchlist;
    },
    onSuccess: async (watchlist) => {
      await queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      setTargetWatchlistId(Number(watchlist.id));
      setSelectedSymbols({});
    },
  });

  const pageError =
    (bootstrapQuery.error as Error | null)?.message ||
    (runsQuery.error as Error | null)?.message ||
    (runMutation.error as Error | null)?.message ||
    (addSelectedToWatchlistMutation.error as Error | null)?.message ||
    (createWatchlistAndAddMutation.error as Error | null)?.message ||
    (runDetailQuery.error as Error | null)?.message ||
    (runSelectsQuery.error as Error | null)?.message ||
    (baselineSelectsQuery.error as Error | null)?.message ||
    (watchlistsQuery.error as Error | null)?.message ||
    "";

  return (
    <main className="flex h-dvh min-h-0 flex-col overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] bg-[#101318]">
        <div className="flex h-12 items-center gap-3 px-3">
          <GlobalAppMenu className="shrink-0" />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-slate-100">智能选股工作台</h1>
            <p className="text-[11px] text-[var(--muted)]">Quant Panel 参数重跑 / 任务跟踪 / 候选结果</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              className="flex h-8 items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-[var(--muted)] hover:text-slate-100"
              onClick={() => {
                void bootstrapQuery.refetch();
                void terminalBootstrapQuery.refetch();
                void runsQuery.refetch();
                void runDetailQuery.refetch();
                void runSelectsQuery.refetch();
                void runLogsQuery.refetch();
                void watchlistsQuery.refetch();
              }}
            >
              <RefreshCw className="size-3.5" />
              刷新
            </button>
            <Link to="/profile" className="flex items-center gap-2 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 py-1.5 hover:bg-white/5">
              <UserAvatar avatarUrl={currentUser?.avatar_url} displayName={displayUserName} />
              <span className="hidden max-w-[110px] truncate text-sm text-slate-100 xl:block">{displayUserName}</span>
            </Link>
          </div>
        </div>
      </header>

      {pageError ? (
        <div className="border-b border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{pageError}</div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)_360px]">
        <aside className="order-2 grid min-h-0 min-w-0 grid-rows-2 border-t border-[var(--border)] bg-[#0f1115] xl:order-1 xl:border-r xl:border-t-0">
          <section className="min-h-0 border-b border-[var(--border)] xl:border-b-0">
            <div className="border-b border-[var(--border)] px-3 py-2">
              <h2 className="text-xs font-semibold text-slate-200">运行记录</h2>
            </div>
            <div className="terminal-scrollbar h-[calc(100%-33px)] overflow-auto">
              <table className="w-full border-collapse text-[11px]">
                <thead className="sticky top-0 z-10 bg-[#151a21] text-slate-300">
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">Run</th>
                    <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">状态</th>
                    <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">结果</th>
                    <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium">交易日</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => {
                    const runId = toRunId(run.id);
                    const active = runId === activeRunId;
                    const status = String(run.status || "").toUpperCase();
                    return (
                      <tr
                        key={runId}
                        className={`cursor-pointer border-b border-[var(--border)]/60 ${active ? "bg-sky-500/10" : "hover:bg-white/4"}`}
                        onClick={() => setActiveRunId(runId)}
                      >
                        <td className="px-2 py-1.5 text-slate-100">#{runId}</td>
                        <td className={`px-2 py-1.5 font-semibold ${statusTone(status)}`}>{status || "--"}</td>
                        <td className="px-2 py-1.5 text-slate-300">{run.result_count ?? "--"}</td>
                        <td className="px-2 py-1.5 text-slate-400">{formatTradeDate(run.trade_date)}</td>
                      </tr>
                    );
                  })}
                  {!runs.length ? (
                    <tr>
                      <td className="px-2 py-4 text-center text-[11px] text-[var(--muted)]" colSpan={4}>
                        暂无运行记录
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          <section className="min-h-0 border-t border-[var(--border)]">
            <div className="border-b border-[var(--border)] px-3 py-2 text-xs font-semibold text-slate-200">运行日志</div>
            <div className="terminal-scrollbar h-[calc(100%-33px)] overflow-auto px-3 py-2 text-[11px]">
              {(runLogsQuery.data?.items || []).map((item, index) => (
                <div key={`${String(item.id || index)}:${item.ts || ""}`} className="border-b border-[var(--border)]/50 py-1.5">
                  <div className="flex items-center gap-2 text-slate-400">
                    <span>{formatTimestamp(item.ts)}</span>
                    <span className={`font-semibold ${statusTone(item.level)}`}>{item.level || "INFO"}</span>
                  </div>
                  <div className="mt-1 whitespace-pre-wrap break-words text-slate-200">{item.message || "--"}</div>
                </div>
              ))}
              {!(runLogsQuery.data?.items || []).length ? (
                <div className="py-6 text-center text-[11px] text-[var(--muted)]">暂无日志</div>
              ) : null}
            </div>
          </section>
        </aside>

        <section className="order-1 flex min-h-0 min-w-0 flex-col border-t border-[var(--border)] bg-[#0d1015] xl:order-2 xl:border-r xl:border-t-0">
          <div className="border-b border-[var(--border)] px-3 py-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-200">
              <span>候选结果 {activeRun ? `#${toRunId(activeRun.id)}` : "（系统基线）"}</span>
              <span className="text-[11px] text-[var(--muted)]">
                显示 {filteredRows.length} / {shownSelects?.total || shownSelects?.items?.length || 0}
              </span>
              <span className="text-[11px] text-[var(--muted)]">勾选 {selectedCount}</span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] xl:grid-cols-[120px_minmax(0,1fr)_100px_120px_84px]">
              <label className="grid gap-1">
                <span className="text-[var(--muted)]">阶段</span>
                <select
                  className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 text-slate-100"
                  value={stageFilter}
                  onChange={(event) => setStageFilter(event.target.value as typeof stageFilter)}
                >
                  <option value="all">全部</option>
                  <option value="accumulation_end">吸筹末期</option>
                  <option value="markup_early">Markup 初期</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-[var(--muted)]">搜索</span>
                <input
                  className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                  value={keywordFilter}
                  onChange={(event) => setKeywordFilter(event.target.value)}
                  placeholder="代码或名称"
                />
              </label>
                  <label className="grid gap-1">
                    <span className="text-[var(--muted)]">评分 ≥</span>
                    <input
                      className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={scoreMinFilterInput}
                      onChange={(event) => setScoreMinFilterInput(event.target.value)}
                    />
                  </label>
              <label className="grid gap-1">
                <span className="text-[var(--muted)]">排序</span>
                <select
                  className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 text-slate-100"
                  value={sortKey}
                  onChange={(event) => setSortKey(event.target.value as QuantSortKey)}
                >
                  <option value="score_desc">评分↓</option>
                  <option value="score_asc">评分↑</option>
                  <option value="code_asc">代码↑</option>
                  <option value="code_desc">代码↓</option>
                </select>
              </label>
              <label className="grid gap-1">
                <span className="text-[var(--muted)]">显示</span>
                <select
                  className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 text-slate-100"
                  value={rowLimit}
                  onChange={(event) => setRowLimit(Math.max(20, toNumber(event.target.value, 100)))}
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={300}>300</option>
                </select>
              </label>
            </div>
              <div className="mt-2 grid grid-cols-1 gap-2 text-[11px] xl:grid-cols-[minmax(0,1fr)_120px_120px_120px]">
              <select
                className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-1.5 text-slate-100"
                value={targetWatchlistId != null ? String(targetWatchlistId) : ""}
                onChange={(event) => setTargetWatchlistId(Number(event.target.value || 0) || null)}
              >
                {watchlists.map((watchlist) => (
                  <option key={watchlist.id} value={watchlist.id}>
                    {watchlist.name}
                  </option>
                ))}
                {!watchlists.length ? <option value="">暂无可用自选股</option> : null}
              </select>
                  <button
                    className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100 disabled:opacity-45"
                    disabled={targetWatchlistId == null || selectedCount <= 0 || addSelectedToWatchlistMutation.isPending}
                onClick={() => {
                  if (targetWatchlistId != null) {
                    addSelectedToWatchlistMutation.mutate(targetWatchlistId);
                  }
                }}
                type="button"
                  >
                    加入自选
                  </button>
                  <button
                    className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100 disabled:opacity-45"
                    disabled={selectedCount <= 0 || createWatchlistAndAddMutation.isPending}
                    onClick={() => {
                      const suggested = `Quant候选_${new Date().toLocaleDateString("zh-CN").replaceAll("/", "")}`;
                      const input = window.prompt("请输入新自选股名称", suggested);
                      const name = String(input || "").trim();
                      if (!name) {
                        return;
                      }
                      createWatchlistAndAddMutation.mutate(name);
                    }}
                    type="button"
                  >
                    新建并加入
                  </button>
                  <button
                    className="h-7 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-[var(--muted)]"
                    onClick={() => setSelectedSymbols({})}
                type="button"
              >
                清空勾选
              </button>
            </div>
          </div>
          <div className="terminal-scrollbar min-h-0 overflow-auto">
            <table className="w-full min-w-[760px] border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-[#151a21] text-[11px] text-slate-300">
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">
                    <input
                      type="checkbox"
                      checked={filteredRows.length > 0 && selectedCount === filteredRows.length}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        if (!checked) {
                          setSelectedSymbols({});
                          return;
                        }
                        const next: Record<string, boolean> = {};
                        for (const row of filteredRows) {
                          next[String(row.ts_code || "").toUpperCase()] = true;
                        }
                        setSelectedSymbols(next);
                      }}
                    />
                  </th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">代码</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">名称</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">阶段</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">评分</th>
                  <th className="border-b border-[var(--border)] px-2 py-2 text-left font-medium">说明</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((item) => {
                  const code = String(item.ts_code || "").toUpperCase();
                  return (
                    <tr key={`${item.ts_code}:${item.stage_tag || ""}`} className="border-b border-[var(--border)]/60 hover:bg-white/4">
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedSymbols[code])}
                          onChange={(event) =>
                            setSelectedSymbols((prev) => ({
                              ...prev,
                              [code]: event.target.checked,
                            }))
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <a className="text-sky-300 hover:underline" href={`/stock?symbol=${encodeURIComponent(item.ts_code)}`}>
                          {item.ts_code}
                        </a>
                      </td>
                      <td className="px-2 py-2 text-slate-200">{item.stock_name || "--"}</td>
                      <td className="px-2 py-2 text-slate-300">{item.stage_name || item.stage_tag || "--"}</td>
                      <td className="px-2 py-2 text-slate-200">{item.score == null ? "--" : Number(item.score).toFixed(1)}</td>
                      <td className="px-2 py-2 text-slate-400">{item.reason_text || "--"}</td>
                    </tr>
                  );
                })}
                {!filteredRows.length ? (
                  <tr>
                    <td className="px-3 py-8 text-center text-[11px] text-[var(--muted)]" colSpan={6}>
                      暂无候选结果
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="order-3 grid min-h-0 min-w-0 grid-rows-[minmax(0,1fr)_240px] border-t border-[var(--border)] bg-[#0f1115] xl:border-t-0">
          <section className="min-h-0 border-b border-[var(--border)]">
            <div className="border-b border-[var(--border)] px-3 py-2">
              <h2 className="text-xs font-semibold text-slate-200">参数配置</h2>
            </div>
            <div className="terminal-scrollbar h-[calc(100%-33px)] overflow-auto px-3 py-2">
              <div className="grid grid-cols-1 gap-2 text-xs">
                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">预设</span>
                  <select
                    className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                    value={form.presetKey}
                    onChange={(event) => {
                      const presetKey = event.target.value;
                      const preset = bootstrapQuery.data?.presets.find((item) => item.key === presetKey);
                      const params = preset?.params || {};
                      setForm((prev) => ({
                        presetKey,
                        pickDate: ymdToInput(String(params.pick_date || inputDateToYmd(prev.pickDate))),
                        limit: toNumber(params.limit, prev.limit ?? 500),
                        minScore: toNumber(params.min_score, prev.minScore ?? 50),
                        maxStocks: toNumber(params.max_stocks, prev.maxStocks ?? 6000),
                      }));
                    }}
                  >
                    {(bootstrapQuery.data?.presets || []).map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.label || item.key}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">选股日</span>
                  <input
                    className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                    type="date"
                    value={form.pickDate}
                    onChange={(event) => setForm((prev) => ({ ...prev, pickDate: event.target.value }))}
                  />
                </label>

                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">结果上限</span>
                <input
                  className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                  type="number"
                  min={20}
                  max={2000}
                  value={form.limit ?? ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, limit: toNumberOrNull(event.target.value, prev.limit ?? 500) }))}
                />
              </label>

                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">最小评分</span>
                <input
                  className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                  type="number"
                  step={0.1}
                  min={0}
                  max={100}
                  value={form.minScore ?? ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, minScore: toNumberOrNull(event.target.value, prev.minScore ?? 50) }))}
                />
              </label>

                <label className="grid gap-1">
                  <span className="text-[var(--muted)]">扫描股票数</span>
                <input
                  className="h-8 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-slate-100"
                  type="number"
                  min={200}
                  max={8000}
                  value={form.maxStocks ?? ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, maxStocks: toNumberOrNull(event.target.value, prev.maxStocks ?? 6000) }))}
                />
              </label>

                <div className="rounded-md border border-[var(--border)] bg-[#10151d] px-2 py-2 text-[11px] text-[var(--muted)]">
                  快照日期 {formatTradeDate(String(bootstrapQuery.data?.freshness?.trade_date || ""))}
                </div>

                <button
                  className="mt-1 flex h-8 items-center justify-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-slate-100 disabled:opacity-45"
                  disabled={runMutation.isPending}
                  onClick={() => runMutation.mutate()}
                >
                  <Rocket className="size-3.5" />
                  提交重跑
                </button>
              </div>
            </div>
          </section>

          <section className="min-h-0">
            <div className="border-b border-[var(--border)] px-3 py-2 text-xs font-semibold text-slate-200">运行详情</div>
            <div className="terminal-scrollbar h-[calc(100%-33px)] overflow-auto px-3 py-2 text-[11px]">
              <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-2 gap-y-1">
                <span className="text-[var(--muted)]">Run</span>
                <span className="text-slate-100">{activeRunId != null ? `#${activeRunId}` : "BASELINE"}</span>
                <span className="text-[var(--muted)]">状态</span>
                <span className={`font-semibold ${statusTone(runDetailQuery.data?.status)}`}>
                  {runDetailQuery.data?.status || (activeRunId == null ? "COMPLETED" : "--")}
                </span>
                <span className="text-[var(--muted)]">排队时间</span>
                <span className="text-slate-300">{formatTimestamp(runDetailQuery.data?.queued_at)}</span>
                <span className="text-[var(--muted)]">开始时间</span>
                <span className="text-slate-300">{formatTimestamp(runDetailQuery.data?.started_at)}</span>
                <span className="text-[var(--muted)]">完成时间</span>
                <span className="text-slate-300">{formatTimestamp(runDetailQuery.data?.finished_at)}</span>
                <span className="text-[var(--muted)]">结果数量</span>
                <span className="text-slate-300">{runDetailQuery.data?.result_count ?? shownSelects?.total ?? "--"}</span>
                <span className="text-[var(--muted)]">交易日</span>
                <span className="text-slate-300">{formatTradeDate(shownTradeDate)}</span>
                <span className="text-[var(--muted)]">错误</span>
                <span className="break-words text-rose-200">{runDetailQuery.data?.error_msg || "--"}</span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
}

function UserAvatar({ avatarUrl, displayName }: { avatarUrl?: string | null; displayName: string }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt={displayName} className="size-6 rounded-full border border-[var(--border)] object-cover" />;
  }
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <span className="flex size-6 items-center justify-center rounded-full bg-[#2a2f37] text-[10px] font-semibold text-slate-100">
      {initials || "U"}
    </span>
  );
}
